import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  type ReactNode,
} from "react";
import { authAPI } from "../services/api";

export type UserRole = "user" | "technician" | "admin";

export interface User {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  department?: string;
  avatar?: string;
  organizationId?: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  login: (
    email: string,
    password: string,
  ) => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
  switchRole: (role: UserRole) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(() => {
    const stored = localStorage.getItem("campus-itsm-user");
    return stored ? JSON.parse(stored) : null;
  });

  useEffect(() => {
    if (user) {
      localStorage.setItem("campus-itsm-user", JSON.stringify(user));
    } else {
      localStorage.removeItem("campus-itsm-user");
    }
  }, [user]);

  useEffect(() => {
    const handleTokenExpired = () => {
      console.log("⚠️ Token expired, forcing logout");
      setUser(null);
      localStorage.removeItem("accessToken");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("campus-itsm-user");
    };

    window.addEventListener("token-expired", handleTokenExpired);
    return () =>
      window.removeEventListener("token-expired", handleTokenExpired);
  }, []);

  const mapRole = (backendRole: string): UserRole => {
    const roleMap: Record<string, UserRole> = {
      student: "user",
      technician: "technician",
      admin: "admin",
      STUDENT: "user",
      TECHNICIAN: "technician",
      ADMIN: "admin",
    };
    return roleMap[backendRole] || "user";
  };

  const login = useCallback(
    async (
      email: string,
      password: string,
    ): Promise<{ success: boolean; error?: string }> => {
      try {
        const response = await authAPI.login(email, password);

        if (response.success) {
          const { user: userData, token, refreshToken } = response.data;

          localStorage.setItem("accessToken", token);
          if (refreshToken) {
            localStorage.setItem("refreshToken", refreshToken);
          }

          const transformedUser: User = {
            id: userData.id,
            name: userData.name || userData.fullName,
            email: userData.email,
            role: mapRole(userData.role),
            department: userData.department,
            avatar: userData.avatar,
            organizationId: userData.organizationId,
          };

          localStorage.setItem(
            "campus-itsm-user",
            JSON.stringify(transformedUser),
          );
          setUser(transformedUser);

          return { success: true };
        }

        return { success: false, error: "Login failed" };
      } catch (error: any) {
        console.error("Login error:", error);
        return {
          success: false,
          error: error.message || "Failed to connect to server",
        };
      }
    },
    [],
  );

  const logout = useCallback(async () => {
    try {
      await authAPI.logout();
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      localStorage.removeItem("accessToken");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("campus-itsm-user");
      localStorage.removeItem("campus-itsm-jwt");
      localStorage.removeItem("campus-itsm-token-expiry");
      setUser(null);
    }
  }, []);

  const switchRole = useCallback(
    (role: UserRole) => {
      if (user) {
        setUser({ ...user, role });
      }
    },
    [user],
  );

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        login,
        logout,
        switchRole,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
